import type { AdminLanguage, SessionInfo } from '@sirius/contracts/admin'
import type { QueryClient } from '@tanstack/react-query'
import type { PropsWithChildren } from 'react'
import { useEffect, useRef, useSyncExternalStore } from 'react'
import { I18nextProvider } from 'react-i18next'
import type { AdminClient } from '~/api/adminClient'
import { ME_QUERY_KEY } from '~/providers/sessionQueries'
import type { PanelI18n } from './createI18n'
import { setFormatLanguage } from './format'

const SIGNED_OUT_KEY = 'panelLanguage'

/** The cached `auth.me`, which names the language of a signed-in user. */
export function useCachedSession(queryClient: QueryClient) {
  return useSyncExternalStore(
    (notify) => queryClient.getQueryCache().subscribe(notify),
    () => queryClient.getQueryData<SessionInfo>(ME_QUERY_KEY),
  )
}

/**
 * The language of a signed-out page: what the host answers
 * (`system.panelLanguage`: the host tenant's default, the platform's, the
 * browser's). Asked once per page load; a failed ask leaves English.
 */
function useSignedOutLanguage(
  client: AdminClient,
  queryClient: QueryClient,
  tenantSlug: string | null,
) {
  const language = useSyncExternalStore(
    (notify) => queryClient.getQueryCache().subscribe(notify),
    () => queryClient.getQueryData<AdminLanguage>([SIGNED_OUT_KEY, tenantSlug]),
  )
  useEffect(() => {
    void queryClient
      .fetchQuery({
        queryKey: [SIGNED_OUT_KEY, tenantSlug],
        queryFn: async () =>
          (await client.system.panelLanguage({ tenantSlug })).adminLanguage,
        staleTime: Infinity,
        retry: false,
      })
      .catch(() => undefined)
    // again when the cache was cleared (a sign-out), so the login page still
    // follows its host
  }, [client, queryClient, tenantSlug, language])
  return language
}

/**
 * Shows the panel in its user's language. A signed-in user's is in their
 * session (`auth.me`: their own, else what the host tenant, the platform and
 * the browser give); before there is a session, or without one, the host's
 * (`system.panelLanguage`). The catalog of a language other than English
 * loads before the switch, then the tree is told: i18next for the texts, the
 * formatter (`format.ts`) for dates and plurals, `<html lang>`. A change the
 * user made refetches what the pages show, so the dates in a table follow.
 *
 * Mount it above `<Refine>`: it reads the query cache directly.
 */
export function PanelLanguageProvider({
  i18n,
  client,
  queryClient,
  tenantSlug,
  children,
}: PropsWithChildren<{
  i18n: PanelI18n
  client: AdminClient
  queryClient: QueryClient
  tenantSlug: string | null
}>) {
  const session = useCachedSession(queryClient)
  const signedOut = useSignedOutLanguage(client, queryClient, tenantSlug)
  // a cleared cache (login, logout) leaves neither for a moment: keep the
  // language the panel shows, or it would flash English
  const shown = useRef<AdminLanguage>('en')
  const language: AdminLanguage =
    session?.adminLanguage ?? signedOut ?? shown.current
  shown.current = language

  useEffect(() => {
    let current = true
    const previous = i18n.language
    void i18n
      .useLanguage(language)
      .then(() => {
        if (!current) {
          return
        }
        setFormatLanguage(language)
        document.documentElement.lang = language
        if (previous !== language) {
          void queryClient.invalidateQueries({
            predicate: ({ queryKey }) =>
              queryKey[0] !== ME_QUERY_KEY[0] && queryKey[0] !== SIGNED_OUT_KEY,
          })
        }
      })
      // a catalog that fails to load leaves the language as it was
      .catch(() => undefined)
    return () => {
      current = false
    }
  }, [i18n, language, queryClient])

  return <I18nextProvider i18n={i18n}>{children}</I18nextProvider>
}
